import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { CsIconModule } from '@components/icon';
import { Catalog, HeroIngredient, MorningNightProduct, Regimen } from '@cureskin/api-client/src/server-api';
import { ApiClientConstant } from '@cureskin/api-client';
import {
  ProductCopyMap,
  TreatmentPlanClassConfig,
  TreatmentPlanItem,
  TreatmentPlanVariant,
} from './report-treatment-plan.types';
import { moveWashCareProductsToEnd, pickHighestPriorityTaggedImage, REGIMEN_SLIDER_TAG } from 'client/helper/regimen-helper';

const NUTRA_FACE_MASK_PRODUCTS = ['MYfqI3YkfS', 'fBTFPnICk2'];
const SEBUM_PRODUCT = 'OnnVCpJ4lN';
const FALLBACK_LANGUAGE = 'en';

type RegimenWithConcern = Regimen & { class?: string; displayConcern?: { name?: string }; products?: Catalog[] };

@Component({
  selector: 'app-report-treatment-plan',
  imports: [CsIconModule],
  templateUrl: './report-treatment-plan.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportTreatmentPlanComponent {
  allocatedDoctorImage = input<string>('');
  displayConcern = input<string>('');
  languagePreference = input<string>('en');
  showArrowUI = input<boolean>(false);
  regimen = input<RegimenWithConcern>();
  experiment = input<TreatmentPlanVariant>();

  private readonly allProducts = computed<MorningNightProduct[]>(() => {
    const regimen = this.regimen();
    return [...(regimen?.morning || []), ...(regimen?.night || [])];
  });

  readonly isHair = computed<boolean>(
    () => this.regimen()?.class === ApiClientConstant.Regimen.Class.HAIR);

  private readonly classPlan = computed<TreatmentPlanClassConfig | undefined>(() => {
    const regimenClass = this.regimen()?.class;
    if (!regimenClass) return undefined;
    return this.experiment()?.treatmentPlanV2?.[regimenClass];
  });

  /** Repair chips: hero-ingredient name + icon, for both face and hair. */
  readonly repairItems = computed<TreatmentPlanItem[]>(() =>
    this.repairIngredients().map((ingredient: HeroIngredient): TreatmentPlanItem => ({
      id: ingredient.objectId,
      text: ingredient.name,
      imageUrl: ingredient.image,
    })));

  /** Routine bullets. Face keeps the legacy single-match behaviour; hair lists every match. */
  readonly routineItems = computed<TreatmentPlanItem[]>(
    () => this.itemsFromMap(this.classPlan()?.ROUTINE, { firstOnly: !this.isHair() }));

  /** Hair uses the same tagged product sentences shown in the regimen product slider. */
  readonly hairRoutineItems = computed<TreatmentPlanItem[]>(() => {
    if (!this.isHair()) return this.routineItems();

    const catalogById = new Map((this.regimen()?.products ?? []).map((product: Catalog) => [product.objectId, product]));
    const orderedRegimenProducts = moveWashCareProductsToEnd(
      this.allProducts().filter((item: MorningNightProduct, index: number, items: MorningNightProduct[]): boolean =>
        items.findIndex((other: MorningNightProduct): boolean => other.product?.objectId === item.product?.objectId) === index),
    );
    const taggedItems = orderedRegimenProducts
      .map((item: MorningNightProduct): TreatmentPlanItem | undefined => {
        const product = catalogById.get(item.product?.objectId);
        const sliderImage = pickHighestPriorityTaggedImage(product?.extraImagesWithTags, REGIMEN_SLIDER_TAG);
        const title = this.resolveConcern(sliderImage?.title);
        return title ? { id: item.product.objectId, text: title } : undefined;
      })
      .filter((item): item is TreatmentPlanItem => !!item);

    return taggedItems.length ? taggedItems : this.routineItems();
  });

  /** Supplement bullets. Face keeps single-match; hair lists every match. */
  readonly supplementItems = computed<TreatmentPlanItem[]>(
    () => this.itemsFromMap(this.classPlan()?.SUPPLEMENT, { firstOnly: !this.isHair() }));

  /** Face always renders the repair block (legacy); hair only when it has repair products. */
  readonly showRepairSection = computed<boolean>(() => !this.isHair() || this.repairItems().length > 0);

  /** The face supplement product id drives the section header (face mask / sebum / generic). */
  private readonly faceSupplementProductId = computed<string | undefined>(() => {
    if (this.isHair()) return undefined;
    const map = this.classPlan()?.SUPPLEMENT;
    if (!map) return undefined;
    return this.allProducts().find(
      (item: MorningNightProduct): boolean => !!item?.product?.objectId && item.product.objectId in map,
    )?.product?.objectId;
  });

  readonly showFaceMaskHeader = computed<boolean>(() => {
    const id = this.faceSupplementProductId();
    return !!id && NUTRA_FACE_MASK_PRODUCTS.includes(id);
  });

  readonly showSebumHeader = computed<boolean>(() => this.faceSupplementProductId() === SEBUM_PRODUCT);

  /** Face repair ingredients: hero ingredients of `isRepair` products, else the last product's. */
  private readonly repairIngredients = computed<HeroIngredient[]>(() => {
    const products = this.allProducts();
    const repairIngredients = products
      .filter((item: MorningNightProduct): boolean => !!item?.product?.isRepair)
      .flatMap((item: MorningNightProduct): HeroIngredient[] =>
        (item.product?.heroIngredients as unknown as HeroIngredient[]) || []);

    if (!repairIngredients.length) {
      const heroIngredientsProduct = [...products].reverse().find(
        (item: MorningNightProduct): boolean =>
          !!(item?.product?.heroIngredients as unknown as HeroIngredient[])?.length);
      if (!heroIngredientsProduct) return [];
      return heroIngredientsProduct.product.heroIngredients as unknown as HeroIngredient[];
    }

    return repairIngredients.filter(
      (ingredient: HeroIngredient, index: number, arr: HeroIngredient[]): boolean =>
        arr.findIndex((other: HeroIngredient): boolean => other?.objectId === ingredient?.objectId) === index,
    );
  });

  /** Products present in the regimen whose id is a key in `map`, mapped to their localized copy. */
  private itemsFromMap(map: ProductCopyMap | undefined, options: { firstOnly?: boolean } = {}): TreatmentPlanItem[] {
    if (!map) return [];
    const language = this.languagePreference();
    const items = this.allProducts()
      .filter((item: MorningNightProduct): boolean => !!item?.product?.objectId && item.product.objectId in map)
      .map((item: MorningNightProduct): TreatmentPlanItem => {
        const copy = map[item.product.objectId];
        return { id: item.product.objectId, text: copy?.[language] ?? copy?.[FALLBACK_LANGUAGE] ?? '' };
      })
      .filter((item: TreatmentPlanItem): boolean => !!item.text);
    return options.firstOnly ? items.slice(0, 1) : items;
  }

  private resolveConcern(text: string | undefined): string {
    const concern = this.regimen()?.displayConcern?.name;
    return text && concern ? text.replaceAll('<primary concern>', concern) : text ?? '';
  }
}
